import { ensurePaymentMethodCard, ensureStripeCustomer } from './data';
import {
  BOOKING_PROCESSES,
  EXTENDED_PRICING_DATA,
  MOORING_BUOY,
  platformOffPercentage,
} from './enums';
import { getDamageWaiverFeeAmount } from './damageWaiver';
import { getSelectedCharityOrganization, isArrayLength, isInstantBooking } from './genericHelpers';
import { fetchAddressFromMapbox } from './helperFunctions';

const strapiBaseURL = process.env.REACT_APP_STRAPI_BASE_URL;

export const getUserLastSeen = user => {
  return !!user?.attributes?.profile?.protectedData?.lastSeenPending;
};

export const getUserBoatDetails = user => {
  const boatDetails = user?.attributes?.profile?.publicData?.boatDetails;
  return boatDetails;
};

export const getUserMembershipID = user => {
  const membershipID = user?.attributes?.profile?.protectedData?.membershipID;
  return membershipID;
};

export const getUserYachtClubs = user => {
  const yachtClubs = user?.attributes?.profile?.publicData?.yachtClubs;
  return yachtClubs;
};

export const isPayoutCharity = user => {
  return user?.attributes?.profile?.publicData?.selectedPayout === 'charity';
};

export const getCharityDetails = user => {
  return getSelectedCharityOrganization(user?.attributes?.profile?.publicData);
};

export const getEffectiveTransactionProcessAlias = listing => {
  const listingAlias = listing?.attributes?.publicData?.transactionProcessAlias || null;
  const listingProcessName = listingAlias ? listingAlias.split('/')[0] : null;
  const bookingProcessNames = Object.values(BOOKING_PROCESSES).map(alias => alias.split('/')[0]);
  const isBookingListing = bookingProcessNames.includes(listingProcessName);
  const isFreeListing = listing?.attributes?.price?.amount === 0 && isBookingListing;
  const isCharityListing = isPayoutCharity(listing?.author);
  return isFreeListing
    ? BOOKING_PROCESSES.FREE_BOOKING_PROCESS_ALIAS
    : isCharityListing
    ? BOOKING_PROCESSES.CHARITY_BOOKING_PROCESS_ALIAS
    : listingAlias;
};

export const getListingWithProcessAlias = listing => {
  const effectiveAlias = getEffectiveTransactionProcessAlias(listing);
  const currentAlias = listing?.attributes?.publicData?.transactionProcessAlias || null;

  if (!effectiveAlias || effectiveAlias === currentAlias || !listing?.attributes) {
    return listing;
  }

  return {
    ...listing,
    attributes: {
      ...listing.attributes,
      publicData: {
        ...listing.attributes.publicData,
        transactionProcessAlias: effectiveAlias,
      },
    },
  };
};

export const getBoatPrice = (listing, currentUser, damageWaiverConfig) => {
  // Retrieve the maximum boat size for the current user
  const maxBoatSize = getUserBoatDetails(currentUser)?.length;
  const listingPrice = listing?.attributes?.price?.amount;
  if (listingPrice === 0) {
    return 0;
  }
  const platformFee = listingPrice * (platformOffPercentage / 100);

  // Check if the listing is for a mooring buoy
  const isBuoy = listing?.attributes?.publicData?.category === MOORING_BUOY;

  // Determine the pricing key based on boat size and listing type
  let pricingKey;
  if (!maxBoatSize) {
    pricingKey = isBuoy ? 'FOR_BUOY' : 'FOR_DOCK';
  } else {
    const isLargeBoat = Number(maxBoatSize) > 45;
    pricingKey = isLargeBoat
      ? isBuoy
        ? 'FOR_LARGE_BUOY' //6000
        : 'FOR_LARGE_DOCK' //10000
      : isBuoy
      ? 'FOR_BUOY' //3000
      : 'FOR_DOCK'; //5000
  }

  // Return the corresponding pricing from the extended pricing data
  return (listingPrice + getDamageWaiverFeeAmount(damageWaiverConfig) + platformFee) / 100;
};

export const getUserDetails = user => {
  // Combine first and last names using optional chaining and nullish coalescing
  const fullName = user?.attributes?.profile?.firstName
    ? `${user?.attributes?.profile?.firstName} ${user?.attributes?.profile?.lastName}`
    : user?.attributes?.profile?.publicData?.fullName;

  // Use optional chaining and nullish coalescing for profile image
  const profileImage =
    user?.profileImage?.attributes?.variants?.default?.url ||
    user?.profileImage?.attributes?.variants?.['square-small2x']?.url ||
    null; // Set to null if no image variants found

  // Use optional chaining and nullish coalescing for email
  const email = user?.attributes?.email || user?.attributes?.profile?.publicData?.email;

  // Extract ID using optional chaining and nullish coalescing
  const id = user?.id?.uuid;
  const firstName = user?.attributes?.profile?.firstName;
  const lastName = user?.attributes?.profile?.lastName;
  const hasDefaultPaymentMethod =
    user?.id &&
    ensureStripeCustomer(user.stripeCustomer).attributes.stripeCustomerId &&
    ensurePaymentMethodCard(user.stripeCustomer.defaultPaymentMethod).id;
  const phoneNumber = user?.attributes?.profile?.protectedData?.phoneNumber;

  // Return object using destructuring assignment and computed property for clarity
  return {
    fullName,
    profileImage,
    email,
    id,
    firstName,
    lastName,
    hasDefaultPaymentMethod,
    phoneNumber,
  };
};

export const getBlogData = blog => {
  // Get the base URL from the configuration.
  const baseUrl = process.env.REACT_APP_STRAPI_BASE_URL;

  // Get the blog images and generate image URLs using the base URL.
  const blogImages = blog?.attributes?.images?.data || [];
  const imagesLink = blogImages?.length && getImagesLink(blogImages, baseUrl);

  // Get the blog title and description.
  const title = blog?.attributes?.title || '';
  const description = blog?.attributes?.description || '';
  const tags = blog?.attributes?.tags || '';
  const createdAt = blog?.attributes?.createdAt || '';

  // Return an object containing the blog title, description, and the first image URL (if available).
  return { title, description, tags, createdAt, image: imagesLink && imagesLink[0] };
};

export async function getListingDetails(listing) {
  // Early return if listing has no valid ID
  if (!listing?.id?.uuid) return null; // Return null for invalid listings

  // Destructure listing data with default values
  const { id = null, attributes = {} } = listing || {};
  const { title = '', publicData = {}, geolocation = {} } = attributes;
  const { location = {}, petsAllowed = false } = publicData;

  const address = await fetchAddressFromMapbox(geolocation.lat, geolocation.lng);
  // Return the extracted listing details
  return {
    listingId: id?.uuid,
    listingName: title,
    address,
    petsAllowed,
    instantBooking: isInstantBooking(listing),
  };
}

export function getBookingDetails(booking) {
  // Early return if listing has no valid ID
  if (!booking?.id?.uuid) return null; // Return null for invalid listings

  // Destructure listing data with default values
  const { id = null, attributes = {} } = booking || {};
  const { start, end } = attributes;

  // Return the extracted listing details
  return {
    start,
    end,
    id,
  };
}

export const hasAllProperties = (obj, props) => {
  return props.every(prop => obj.hasOwnProperty(prop));
};

export const extractAndFormatBoundsFromString = queryString => {
  // Extract the query string part from the URL
  const queryParamsPart = queryString.split('?')[1];

  // Use URLSearchParams to parse the query parameters
  const params = new URLSearchParams(queryParamsPart);
  const bounds = params.get('bounds');

  // Check if bounds parameter exists
  if (!bounds) {
    console.log('Bounds parameter not found in the URL');
    return null;
  }

  // Split the bounds string into individual coordinates
  const coordinates = bounds.split(',');

  // Ensure we have exactly four coordinates for NE and SW bounds
  if (coordinates.length !== 4) {
    console.log('Invalid bounds format');
    return null;
  }

  // Format the coordinates: NE lat, NE lng, SW lat, SW lng
  const formattedBounds = coordinates.map(coord => coord.trim()).join(',');

  return formattedBounds;
};

export const getImagesLink = (images, url) => {
  // Generate an array of image URLs using the base URL and the image object's URL attribute.
  const imageUrls =
    isArrayLength(images) &&
    images.map(image => {
      const imageUrl = `${url}${image?.attributes?.url}`;
      return imageUrl;
    });

  // Return the array of image URLs.
  return imageUrls;
};
export const getUserFullName = user => {
  const firstName = user?.attributes?.profile?.firstName || '';
  const lastName = user?.attributes?.profile?.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || 'N/A';
};

export const getPriceAndAssignListingType = listing => {
  return listing?.attributes?.publicData?.customPriceAmount;
};

export const getMarketingEventReminder = user => {
  return user?.attributes?.profile?.protectedData?.marketingEventReminder || false;
};

export const isPhoneNumberVerified = currentUser => {
  return currentUser?.attributes?.profile?.protectedData?.isPhoneNumberVerified;
};
export const getUserPhoneNumber = currentUser => {
  return currentUser?.attributes?.profile?.protectedData?.phoneNumber;
};

export const hidePhoneVerification = currentUser => {
  return currentUser?.attributes?.profile?.protectedData?.hidePhoneVerificationModal;
};

export const validateCustomListingPrice = price => {
  if (!price || price._sdkType !== 'Money') {
    return false; // Ensure price is valid
  }

  const { amount } = price;
  return amount === 3000 || amount === 5000 ? false : price;
};

export const getUserNotificationPreferences = user => {
  const phone = user?.attributes?.profile?.protectedData?.notifications?.phone
    ? user?.attributes?.profile?.protectedData?.notifications?.phone
    : false;

  const email = user?.attributes?.profile?.protectedData?.notifications?.email
    ? user?.attributes?.profile?.protectedData?.notifications?.email
    : false;

  return {
    phone,
    email,
  };
};

export const getTextNotificationsOptedIn = currentUser => {
  return currentUser?.attributes?.profile?.protectedData?.textNotificationsOptedIn || false;
};

export function retrieveCustomerId(user) {
  if (!user || !user.id) {
    return null;
  }
  const { stripeCustomerId } = user.stripeCustomer?.attributes || {};
  return stripeCustomerId || null;
}
